"use client"

import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import Sidebar from "./Sidebar"
import Header from "./Header"
import { Line, Bar } from "react-chartjs-2"
import axios from "axios"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js"
import { format } from "date-fns"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement)

const SELECTED_PROJECT_STORAGE_KEY = "magnasight_selected_project"

const getStoredProjectId = () => {
  try {
    return JSON.parse(localStorage.getItem(SELECTED_PROJECT_STORAGE_KEY) || "{}").project_id || null
  } catch {
    return null
  }
}

const persistSelectedProject = (project) => {
  localStorage.setItem(SELECTED_PROJECT_STORAGE_KEY, JSON.stringify({
    project_id: project.value,
    billing_id: project.billing_id,
  }))
}

// Skeleton Components
const BillingSummarySkeleton = () => (
  <div className="animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
    <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
    <div className="h-8 bg-gray-200 rounded w-full mb-2"></div>
    <div className="h-3 bg-gray-200 rounded w-5/6"></div>
  </div>
)

const TopServicesSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
    <div className="space-y-3">
      <div className="flex items-center">
        <div className="w-3 h-3 bg-gray-200 rounded-full mr-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/3 mr-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
      </div>
      <div className="flex items-center">
        <div className="w-3 h-3 bg-gray-200 rounded-full mr-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/4 mr-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
      </div>
    </div>
  </div>
)

const BarChartSkeleton = () => {
  // Static heights for consistent appearance
  const barHeights = [
    85, 92, 76, 88, 95, 73, 81, 97, 90, 78,
    82, 69, 74, 99, 87, 91, 77, 84, 93, 80,
    71, 96, 79, 100, 89, 72, 83, 98, 86, 94
  ];

  return (
    <div className="animate-pulse h-64 md:h-72 flex items-end justify-between px-4 py-8 space-x-1">
      {/* Generate 30 thin bars with static heights */}
      {barHeights.map((height, i) => (
        <div key={i} className="flex flex-col items-center space-y-2 flex-1 max-w-[24px]">
          {/* Stacked bars to simulate grouped bar chart */}
          <div className="w-full flex flex-col space-y-0.5">
            <div className="bg-gray-200 rounded-t w-full" style={{ height: `${height}px` }}></div>
            <div className="bg-gray-300 w-full" style={{ height: `${Math.floor(height * 0.6)}px` }}></div>
            <div className="bg-gray-200 rounded-b w-full" style={{ height: `${Math.floor(height * 0.4)}px` }}></div>
          </div>
          {/* X-axis label - only show every 5th label to avoid crowding */}
          {i % 5 === 0 && <div className="h-2 bg-gray-200 rounded w-6"></div>}
        </div>
      ))}
    </div>
  );
};

const ResourcesBillingSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
    <hr className="mb-4" />
    <div className="space-y-3">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="flex justify-between items-center py-2">
          <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
        </div>
      ))}
    </div>
  </div>
)

// Helper functions
const formatCurrency = (value) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  return `${date.getDate()} ${date.toLocaleString("default", { month: "short" })}`
}

// Color palette for services
const serviceColors = {
  "Cloud SQL": "#4F7CFF",
  Networking: "#FF7C4F",
  "Compute Engine": "#36A2EB",
  "VM Manager": "#9966FF",
  "Cloud Storage": "#FF9F40",
  "Cloud Logging": "#4BC0C0",
  "Cloud Monitoring": "#F87979",
  "Source Repository": "#7FC8A9",
}

// Get a color for a service, with fallback
const getServiceColor = (serviceName, index) => {
  return serviceColors[serviceName] || `hsl(${(index * 360) / Object.keys(serviceColors).length}, 70%, 60%)`
}

// Get current month in YYYY-MM format
const getCurrentMonth = () => {
  const now = new Date()
  return format(now, "yyyy-MM")
}

// Check if selected month is current month
const isCurrentMonth = (selectedMonth) => {
  const currentMonth = getCurrentMonth()
  return selectedMonth === currentMonth
}

const savingsOptions = [
  { key: "negotiated_savings", label: "Savings programmes (negotiated pricing)" },
  { key: "spend_based_cud_savings", label: "Spend-based CUD discounts" },
  { key: "legacy_cud_savings", label: "Legacy spend-based CUD credits" },
  { key: "free_tier_savings", label: "Free tier credits" },
  { key: "promotional_savings", label: "Promotional credits" },
  { key: "sud_savings", label: "Sustained use discounts (SUDs)" },
  { key: "spending_based_savings", label: "Spending-based discounts" },
]

function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedProject = searchParams.get("project")
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true))
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCumulative, setShowCumulative] = useState(false)
  const [chartType, setChartType] = useState("bar")
  const [processedData, setProcessedData] = useState(null)
  const [isChartLoading, setIsChartLoading] = useState(true)
  const [selectedProject, setSelectedProject] = useState(requestedProject)
  const [selectedBillingId, setSelectedBillingId] = useState(null)
  const [availableProjects, setAvailableProjects] = useState([])
  const [billingData, setBillingData] = useState(null)
  const [billingSummary, setBillingSummary] = useState({
    totalCost: 0,
    topService: null,
    topServiceCost: 0,
    secondService: null,
    secondServiceCost: 0,
    serviceCount: 0,
  })
  const [currentMonthName, setCurrentMonthName] = useState(format(new Date(), "MMMM yyyy"))
  const [invoiceData, setInvoiceData] = useState(null)

  const toggleSidebar = () => {
    setSidebarOpen((prevState) => !prevState)
  }

  // Fetch tickets
  useEffect(() => {
    const fetchTickets = async () => {
      try {
        setLoading(true)
        const user = JSON.parse(localStorage.getItem("user"))
        const userId = user ? user.id_user : null

        if (userId) {
          const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/tickets/user/${userId}`)
          setTickets(response.data)
        } else {
          console.error("User ID not found. Unable to fetch tickets.")
          setTickets([])
        }
      } catch (error) {
        console.error("Error fetching tickets:", error)
        setError("Failed to fetch tickets. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    fetchTickets()
  }, [])

  // Fetch projects for dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // Get user ID from localStorage
        const user = JSON.parse(localStorage.getItem("user") || "{}")
        const userId = user.id_user

        if (!userId) {
          console.error("User ID not found in localStorage")
          return
        }

        // Ubah endpoint ke /api/users/project/{id_user}
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/project/${userId}`)

        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`)
        }

        const data = await response.json()
        // data: [{ project_id, billing_id }, ...]

        // Simpan ke dropdown (label: project_id, value: project_id, billing_id)
        const baseProjects = data.map((item) => ({
          value: item.project_id,
          label: item.project_id,
          billing_id: item.billing_id,
        }))

        const projects = [
          ...(baseProjects.length > 1
            ? [{ value: "all", label: "All Projects", billing_id: baseProjects[0].billing_id }]
            : []),
          ...baseProjects,
        ]

        setAvailableProjects(projects)

        // An explicit URL filter wins; otherwise preserve the project selected in another feature.
        if (projects.length > 0) {
          const requested = projects.find((project) => project.value === requestedProject)
          const stored = projects.find((project) => project.value === getStoredProjectId())
          const defaultProject = requested || (stored && projects.find((p) => p.value === stored.value)) || projects[0]
          setSelectedProject(defaultProject.value)
          setSelectedBillingId(defaultProject.billing_id)
        }
      } catch (error) {
        console.error("Error fetching projects:", error)
      }
    }

    fetchProjects()
  }, [requestedProject]) // Run once on component mount and when URL filter changes

  // Update billing id saat project berubah
  useEffect(() => {
    if (!selectedProject || availableProjects.length === 0) return
    const found = availableProjects.find((p) => p.value === selectedProject)
    if (found && found.billing_id) {
      setSelectedBillingId(found.billing_id)
      persistSelectedProject(found)
    }
  }, [selectedProject, availableProjects])

  // Fetch billing data from API
  useEffect(() => {
    const fetchBillingData = async () => {
      if (!selectedProject || !selectedBillingId) return

      setIsChartLoading(true)
      try {
        // Ambil billingAccountId dari selectedBillingId, bukan localStorage
        const billingAccountId = selectedBillingId || "01E318-E504D7-6A5BBF"
        const currentMonth = getCurrentMonth()
        setCurrentMonthName(format(new Date(currentMonth), "MMMM yyyy"))

        const projectParam = selectedProject && selectedProject !== "all" ? `&project_id=${selectedProject}` : ""
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/billing/billing-by-service-by-project?billing_account_id=${billingAccountId}${projectParam}&year_month=${currentMonth}-01`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
        )

        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`)
        }

        const data = await response.json()
        setBillingData(data)

        console.log("Fetched billing data:", data)
      } catch (error) {
        console.error("Error fetching billing data:", error)
      } finally {
        setIsChartLoading(false)
      }
    }

    fetchBillingData()
  }, [selectedProject, selectedBillingId])

  // Fetch invoice summary data - only for historical months
  useEffect(() => {
    const fetchInvoiceSummary = async () => {
      if (!selectedProject || !selectedBillingId) return

      // Only fetch invoice data for historical months (Dashboard always shows current month)
      const currentMonth = getCurrentMonth()
      if (isCurrentMonth(currentMonth)) {
        console.log("Current month detected, skipping invoice API call")
        return
      }

      try {
        // Ambil billingAccountId dari selectedBillingId, bukan localStorage
        const billingAccountId = selectedBillingId || "01E318-E504D7-6A5BBF"
        // Get current month in YYYYMM format
        const invoiceMonth = currentMonth.replace("-", "")

        const projectParam = selectedProject && selectedProject !== "all" ? `&project_id=${selectedProject}` : ""
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/billing/summary-billing-invoices?billing_account_id=${billingAccountId}${projectParam}&invoice_month=${invoiceMonth}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
        )

        if (!response.ok) {
          throw new Error(`Invoice API request failed with status ${response.status}`)
        }

        const data = await response.json()
        setInvoiceData(data)
        console.log("Fetched invoice data:", data)

        // Update billing summary with invoice data for historical months
        if (data && data["monthly invoices data"] && data["monthly invoices data"].length > 0) {
          const invoiceTotal = data["monthly invoices data"][0].sum

          setBillingSummary((prev) => ({
            ...prev,
            totalCost: invoiceTotal,
          }))
        }
      } catch (error) {
        console.error("Error fetching invoice summary:", error)
      }
    }

    fetchInvoiceSummary()
  }, [selectedProject, selectedBillingId])

  // Process the billing data whenever it changes
  useEffect(() => {
    if (billingData) {
      processBillingData()
    }
  }, [billingData, showCumulative, chartType])

  // Process the billing data from the API
  const processBillingData = () => {
    try {
      console.log("Processing billing data...")

      if (!billingData || !billingData["billing by service and project data"]) {
        console.error("Billing data is not available or in unexpected format")
        return
      }

      // Get all dates and sort them chronologically
      const dates = Object.keys(billingData["billing by service and project data"]).sort()

      // Get all unique service names
      const allServices = new Set()
      for (const date of dates) {
        const dayServices = billingData["billing by service and project data"][date]
        dayServices
          .filter((service) => service.service_name.toLowerCase() !== "invoice")
          .forEach((service) => {
            if (service.service_name) {
              allServices.add(service.service_name)
            }
          })
      }
      const serviceNames = Array.from(allServices)

      // Process data for each date
      const dailyData = dates.map((date) => {
        const dayServices = billingData["billing by service and project data"][date]

        // Group and sum subtotals by service name with all savings/credits applied
        const serviceCosts = {}
        dayServices.forEach((service) => {
          const { service_name, subtotal } = service
          if (!service_name) return
          if (!serviceCosts[service_name]) {
            serviceCosts[service_name] = 0
          }
          const selectedSavings = savingsOptions.reduce(
            (sum, option) => sum + Number(service[option.key] || 0),
            0,
          )
          serviceCosts[service_name] += Number(subtotal || 0) + selectedSavings
        })

        return {
          date,
          formattedDate: formatDate(date),
          services: serviceCosts,
          total: Object.values(serviceCosts).reduce((sum, cost) => sum + Number(cost), 0),
        }
      })

      // Apply cumulative calculation if needed
      let processedBillingData = dailyData
      if (showCumulative) {
        processedBillingData = dailyData.map((day, index, array) => {
          if (index === 0) return day

          const cumulativeServices = {}

          // Initialize with current day's services
          Object.keys(day.services).forEach((service) => {
            cumulativeServices[service] = day.services[service]
          })

          // Add previous days' values
          for (let i = 0; i < index; i++) {
            Object.keys(array[i].services).forEach((service) => {
              if (!cumulativeServices[service]) {
                cumulativeServices[service] = 0
              }
              cumulativeServices[service] += array[i].services[service]
            })
          }

          return {
            ...day,
            services: cumulativeServices,
            total: Object.values(cumulativeServices).reduce((sum, val) => sum + Number(val), 0),
          }
        })
      }

      // Prepare chart data
      const chartData = {
        labels: processedBillingData.map((day) => day.formattedDate),
        datasets: serviceNames.map((service, index) => ({
          label: service,
          data: processedBillingData.map((day) => day.services[service] || 0),
          backgroundColor: getServiceColor(service, index),
          borderColor: getServiceColor(service, index),
          borderWidth: chartType === "line" ? 2 : 1,
          fill: chartType === "line" ? false : true,
          tension: chartType === "line" ? 0.4 : 0,
          stack: chartType === "bar" ? "stack1" : undefined,
        })),
      }

      // Prepare pie chart data
      const totalsByService = {}
      serviceNames.forEach((service) => {
        totalsByService[service] = dailyData.reduce((sum, day) => sum + (day.services[service] || 0), 0)
      })

      // Calculate total cost for billing summary
      const totalCost = Object.values(totalsByService).reduce((sum, cost) => sum + Number(cost), 0)

      // Sort services by cost for summary
      const sortedServices = Object.entries(totalsByService).sort((a, b) => b[1] - a[1])

      // Get top services for summary
      const topService = sortedServices.length > 0 ? sortedServices[0][0] : null
      const topServiceCost = sortedServices.length > 0 ? sortedServices[0][1] : 0
      const secondService = sortedServices.length > 1 ? sortedServices[1][0] : null
      const secondServiceCost = sortedServices.length > 1 ? sortedServices[1][1] : 0

      // Update billing summary - use calculated total for current month
      setBillingSummary((prev) => ({
        ...prev,
        totalCost: isCurrentMonth(getCurrentMonth()) ? totalCost : prev.totalCost,
        topService,
        topServiceCost,
        secondService,
        secondServiceCost,
        serviceCount: serviceNames.length,
      }))

      const pieChartData = {
        labels: serviceNames,
        datasets: [
          {
            data: serviceNames.map((service) => totalsByService[service]),
            backgroundColor: serviceNames.map((service, index) => getServiceColor(service, index)),
            borderColor: serviceNames.map((service, index) => getServiceColor(service, index)),
            borderWidth: 1,
          },
        ],
      }

      // Prepare top resources billing
      const topResources = sortedServices.slice(0, 5).map(([name, cost]) => ({
        name,
        amount: formatCurrency(cost),
      }))

      setProcessedData({
        chartData,
        pieChartData,
        topResources,
        totalCost,
      })

      console.log("Processed data:", {
        dates: dates.length,
        services: serviceNames.length,
        dailyData: dailyData.length,
        chartData: chartData.labels.length,
      })
    } catch (error) {
      console.error("Error processing billing data:", error)
    }
  }

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: typeof window !== "undefined" ? window.innerWidth >= 768 : true,
        position: "top",
        labels: {
          boxWidth: 10,
          usePointStyle: true,
          pointStyle: "circle",
          font: { size: 11 },
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || ""
            const value = context.raw
            return `${label}: ${formatCurrency(value)}`
          },
          footer: (tooltipItems) => {
            const total = tooltipItems.reduce((sum, item) => sum + item.raw, 0)
            return `Total: ${formatCurrency(total)}`
          },
        },
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#111",
        bodyColor: "#333",
        footerColor: "#111",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        cornerRadius: 8,
      },
      title: {
        display: false,
        text: showCumulative ? "Cumulative Billing by Service" : "Daily Billing by Service",
        font: { size: 16, weight: "bold" },
        padding: { top: 10, bottom: 20 },
        color: "#333",
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxRotation: 0, // Horizontal labels
          minRotation: 0,
          padding: 8,
          font: { size: typeof window !== "undefined" && window.innerWidth < 640 ? 10 : 11 },
          autoSkip: true,
          maxTicksLimit: typeof window !== "undefined" && window.innerWidth < 640 ? 6 : 15,
        },
        stacked: chartType === "bar",
      },
      y: {
        stacked: chartType === "bar",
        ticks: {
          callback: (value) => `Rp${(value / 1000).toFixed(0)}K`,
          font: { size: typeof window !== "undefined" && window.innerWidth < 640 ? 10 : 11 },
        },
        grid: { color: "rgba(0, 0, 0, 0.05)" },
      },
    },
    elements: {
      point: {
        radius: chartType === "line" ? 3 : 0,
        hoverRadius: chartType === "line" ? 5 : 0,
      },
      line: { tension: 0.4 },
      bar: { borderRadius: 4 },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
  }

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || ""
            const value = context.raw
            const total = context.dataset.data.reduce((a, b) => a + b, 0)
            const percentage = ((value / total) * 100).toFixed(1)
            return `${label}: ${formatCurrency(value)} (${percentage}%)`
          },
        },
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#111",
        bodyColor: "#333",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
      },
    },
    cutout: "70%",
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />

      <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

        {/* Main Content */}
        <div
          className={`flex-1 min-w-0 max-w-full overflow-x-hidden p-3.5 sm:p-6 transition-all duration-300 pt-16 sm:pt-20 ${sidebarOpen ? "lg:ml-64" : "ml-0"}`}
        >
          {/* Billing Summary Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 sm:gap-4 mb-3 sm:mb-6">
            {/* Project Selection */}
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
              <h2 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3 text-gray-900">Project Selection</h2>
              <div className="relative">
                <select
                  id="project-select"
                  value={selectedProject || ""}
                  onChange={(e) => {
                    const project = e.target.value
                    setSelectedProject(project)
                    setSearchParams({ project })
                  }}
                  className="w-full appearance-none bg-white border border-gray-200 rounded-md pl-3 pr-8 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 min-h-[36px] sm:min-h-[40px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="" disabled>
                    Select a project
                  </option>
                  {availableProjects.map((project) => (
                    <option key={project.value} value={project.value}>
                      {project.label}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Billing Summary */}
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
              <h2 className="text-sm sm:text-base font-semibold mb-1 text-gray-900">Billing Summary</h2>
              <p className="text-xs md:text-sm text-gray-500 mb-2">{currentMonthName}</p>
              {isChartLoading ? (
                <BillingSummarySkeleton />
              ) : (
                <div>
                  <p className="text-xl md:text-2xl font-bold">{formatCurrency(billingSummary.totalCost)}</p>
                  <p className="text-xs md:text-sm text-gray-500 mt-2">
                    Total billing across {billingSummary.serviceCount} services
                  </p>
                </div>
              )}
            </div>

            {/* Top Services */}
            <div className="bg-white p-3 md:p-4 rounded-lg shadow-md">
              <h2 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Top Services</h2>
              {isChartLoading ? (
                <TopServicesSkeleton />
              ) : billingSummary.topService ? (
                <div>
                  <div className="flex items-center mb-3">
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: getServiceColor(billingSummary.topService, 0) }}
                    ></div>
                    <p className="font-medium text-sm md:text-base">{billingSummary.topService}:</p>
                    <p className="ml-2 font-bold text-sm md:text-base">
                      {formatCurrency(billingSummary.topServiceCost)}
                    </p>
                  </div>

                  {billingSummary.secondService && (
                    <div className="flex items-center">
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: getServiceColor(billingSummary.secondService, 1) }}
                      ></div>
                      <p className="font-medium text-sm md:text-base">{billingSummary.secondService}:</p>
                      <p className="ml-2 font-bold text-sm md:text-base">
                        {formatCurrency(billingSummary.secondServiceCost)}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No billing data available</p>
              )}
            </div>
          </div>

          {/* Billing Chart Section */}
          <div className="bg-white p-3 md:p-4 rounded-lg shadow-md mb-4 md:mb-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-3 md:gap-0">
              <h2 className="text-base md:text-lg font-semibold">Billing Reports</h2>
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-pressed={showCumulative}
                    onClick={() => setShowCumulative((v) => !v)}
                    className="relative w-10 h-5 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-full transition"
                    tabIndex={0}
                  >
                    <span
                      className={`block w-10 h-5 rounded-full transition-colors duration-200 ${
                        showCumulative ? "bg-blue-600" : "bg-gray-300"
                      }`}
                    ></span>
                    <span
                      className={`absolute left-0 top-0.5 w-4 h-4 rounded-full bg-white shadow transform transition-transform duration-200 ${
                        showCumulative ? "translate-x-5" : "translate-x-1"
                      }`}
                    ></span>
                  </button>
                  <label htmlFor="show-cumulative" className="text-sm select-none cursor-default">
                    Show cumulative
                  </label>
                </div>
                <div className="flex gap-2">
                  <button
                    className={`p-2 border rounded-md ${
                      chartType === "line" ? "bg-blue-50 text-blue-600 border-blue-200" : "border-gray-200"
                    }`}
                    onClick={() => setChartType("line")}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="md:w-6 md:h-6">
                      <path
                        d="M3 16.5L9 10.5L13 14.5L21 6.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    className={`p-2 border rounded-md ${
                      chartType === "bar" ? "bg-blue-50 text-blue-600 border-blue-200" : "border-gray-200"
                    }`}
                    onClick={() => setChartType("bar")}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="md:w-6 md:h-6">
                      <path
                        d="M8 13V17M12 9V17M16 5V17"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <hr className="mb-4" />

            {/* Chart and Resources Section */}
            <div className="flex flex-col lg:flex-row lg:gap-4">
              {/* Billing Chart */}
              <div className="flex-1 lg:w-3/4">
                <div className="h-64 md:h-72">
                  {isChartLoading ? (
                    <BarChartSkeleton />
                  ) : processedData && processedData.chartData ? (
                    chartType === "bar" ? (
                      <Bar data={processedData.chartData} options={chartOptions} />
                    ) : (
                      <Line data={processedData.chartData} options={chartOptions} />
                    )
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500 text-sm md:text-base">
                        No billing data available for the selected period
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Resources Billing */}
              <div className="lg:w-1/4 mt-4 lg:mt-0 lg:border-l lg:pl-4">
                {isChartLoading ? (
                  <ResourcesBillingSkeleton />
                ) : (
                  <>
                    <h2 className="text-base md:text-lg font-semibold mb-2">Resources Billing</h2>
                    <hr className="mb-4" />
                    {processedData && processedData.topResources && processedData.topResources.length > 0 ? (
                      <ul className="text-xs md:text-sm space-y-2 divide-y divide-gray-300">
                        {processedData.topResources.map((resource, index) => (
                          <li key={index} className="flex justify-between py-2">
                            <span className="truncate mr-2">{resource.name}</span>
                            <span className="whitespace-nowrap">{resource.amount}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-500 text-sm">No resources data available</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Ticket Overview Section */}
          <div className="mt-4 md:mt-6">
            <div className="bg-white p-3 md:p-4 rounded-lg shadow-md h-64 md:h-80 overflow-auto">
              <h2 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Ticket Overview</h2>
              <hr className="mb-4" />

              {/* Loading and error states */}
              {loading && <p className="text-gray-500 text-sm">Loading tickets...</p>}
              {error && <p className="text-red-500 text-sm">Error: {error}</p>}

              {/* Render tickets */}
              <div className="grid grid-cols-1 gap-3 md:gap-4">
                {tickets.length > 0 ? (
                  tickets.map((ticket) => (
                    <div key={ticket.id} className="bg-white p-3 md:p-4 rounded-lg shadow-sm hover:shadow-md">
                      <h3 className="font-semibold text-sm md:text-base">{ticket.product_list}</h3>
                      <p className="text-xs md:text-sm text-gray-600 mt-1">{ticket.describe_issue}</p>
                      <div className="mt-2 text-xs md:text-sm">
                        <span
                          className={`badge ${
                            ticket.status === "Open" ? "bg-red-500" : "bg-green-500"
                          } text-white py-1 px-2 md:px-3 rounded-full text-xs`}
                        >
                          {ticket.status}
                        </span>
                        <span className="ml-2 md:ml-4 text-gray-500">{ticket.priority}</span>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        Created on: {new Date(ticket.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No tickets available.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
